import { Flex } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  TrackedDrawer,
  TrackedDrawerProps
} from '@tools/ui/components/common/TrackedDrawer';
import { theme as semanticTheme, theme } from '../../theme/semanticTokens';
import { IconType } from '../../types/icons/IconTypes.enum';
import { Typography } from '../../Typography';
import { Button } from '../Button';

export interface DrawerProps extends Pick<TrackedDrawerProps, 'analytics'> {
  open: boolean;
  title?: React.ReactNode;
  children: React.ReactNode;
  submitText?: string;
  cancelText?: string;
  submitDisabled?: boolean;
  submitLoading?: boolean;
  cancelDisabled?: boolean;
  onSubmit?: (e?: React.MouseEvent) => void;
  onCancel?: () => void;
  onClose?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  width?: number | string;
  className?: string;
  placement?: 'top' | 'right' | 'bottom' | 'left';
  destroyOnClose?: boolean;
  hideFooter?: boolean;
  layout?: 'default' | 'split';
  buttonsRightAligned?: boolean;
  /** Extra action buttons rendered in the header between the title and the close button. */
  extraActions?: React.ReactNode;
  /** Allow the user to resize the drawer by dragging its left edge. */
  resizable?: boolean;
  /** Minimum width when resizable. Defaults to the initial width value. */
  minWidth?: number;
}

const StyledDrawer = styled(TrackedDrawer)`
  .ant-drawer-header {
    border-bottom: 1px solid ${theme.dime.colors.border.static};
  }

  .ant-drawer-body {
    padding: ${theme.dime.spacing.xl};
  }

  .ant-drawer-footer {
    border-top: 1px solid ${theme.dime.colors.border.static};
    padding: ${theme.dime.spacing.l};
    display: flex;
    justify-content: flex-end;
    gap: ${theme.dime.spacing.m};
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const ContainerFooter = styled(Flex)`
  width: 100%;
`;

const Resizer = styled.div`
  position: absolute;
  width: 5px;
  top: 0;
  left: -5px;
  bottom: 0;
  z-index: 100;
  cursor: ew-resize;
  background-color: ${theme.dime.colors.border.static};

  &:hover {
    background-color: ${theme.dime.colors.action.primary};
  }
`;

export const Drawer: React.FC<DrawerProps> = ({
  open,
  title,
  children,
  submitText = 'Submit',
  cancelText = 'Cancel',
  submitDisabled = false,
  submitLoading = false,
  cancelDisabled = false,
  onSubmit,
  onCancel,
  onClose,
  width = 520,
  className,
  placement = 'right',
  destroyOnClose,
  hideFooter = false,
  layout = 'default',
  buttonsRightAligned = false,
  extraActions,
  resizable = false,
  minWidth,
  analytics
}) => {
  const initialWidth = typeof width === 'number' ? width : 520;
  const effectiveMinWidth = minWidth ?? initialWidth;
  const [drawerWidth, setDrawerWidth] = useState(initialWidth);
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    if (!open) {
      setDrawerWidth(initialWidth);
    }
  }, [open, initialWidth]);

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;
      const offsetRight =
        document.body.offsetWidth - (e.clientX - document.body.offsetLeft);
      if (offsetRight >= effectiveMinWidth) {
        setDrawerWidth(offsetRight);
      }
    },
    [isResizing, effectiveMinWidth]
  );

  const onMouseUp = useCallback(() => setIsResizing(false), []);

  useEffect(() => {
    if (!resizable) return;
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [resizable, onMouseMove, onMouseUp]);

  const hasFooter = !hideFooter && (!!onCancel || !!onSubmit);
  const footer = hasFooter && (
    <ContainerFooter
      justify={buttonsRightAligned ? 'flex-end' : 'space-between'}
      gap={semanticTheme.dime.spacing.l}
      align="center"
    >
      {!!onCancel && (
        <Button
          variant="secondary"
          onClick={onCancel}
          disabled={cancelDisabled}
        >
          {cancelText}
        </Button>
      )}
      {!!onSubmit && (
        <Button
          onClick={onSubmit}
          loading={submitLoading}
          variant="primary"
          disabled={submitDisabled}
        >
          {submitText}
        </Button>
      )}
    </ContainerFooter>
  );

  const customHeader = (
    <HeaderContainer>
      <Typography variant="heading3Bold">{title}</Typography>
      <Flex gap={semanticTheme.dime.spacing.s} align="center">
        {extraActions}
        <Button
          variant="secondary"
          onClick={onClose}
          iconOnly={IconType.Close}
          size="middle"
        />
      </Flex>
    </HeaderContainer>
  );

  const drawerStyles =
    layout === 'split'
      ? {
          body: {
            padding: 0,
            display: 'flex' as const,
            flexDirection: 'column' as const,
            height: '100%',
            overflow: 'hidden'
          }
        }
      : undefined;

  return (
    <StyledDrawer
      title={customHeader}
      open={open}
      onClose={(e) => {
        onClose?.(e);
      }}
      footer={footer}
      width={resizable ? drawerWidth : width}
      className={className}
      placement={placement}
      closable={false}
      destroyOnClose={destroyOnClose}
      styles={drawerStyles}
      analytics={analytics}
    >
      {resizable && (
        <Resizer onMouseDown={() => setIsResizing(true)} />
      )}
      {children}
    </StyledDrawer>
  );
};
